import type { NotificationView } from '../types'
import type { CopyRenderer, NotificationCopy } from './types'
import { NOTIFICATION_TYPE } from '../entity'
import {
  renderAssignmentAllCompletedCopy,
  renderAssignmentAssignedCopy,
  renderAssignmentClassRequestedRecipientCopy,
  renderAssignmentDueSoonCopy,
  renderAssignmentGradedCopy,
  renderAssignmentIncompleteCopy,
  renderAssignmentNeedsGradingCopy,
  renderAssignmentNotStartedCopy,
  renderChallengeAssignedCopy,
  renderChallengeDueSoonCopy,
} from './assignment'
import {
  renderConnectAcceptedRecipientCopy,
  renderConnectChildNameUpdatedActorCopy,
  renderConnectChildNameUpdatedRecipientCopy,
  renderConnectChildPinUpdatedActorCopy,
  renderConnectChildPinUpdatedRecipientCopy,
  renderConnectParentMessageRecipientCopy,
  renderConnectRejectedRecipientCopy,
  renderConnectRemovalRequestedRecipientCopy,
  renderConnectRequestedActorCopy,
  renderConnectRequestedRecipientCopy,
  renderConnectStudentRemovedRecipientCopy,
} from './connect'
import {
  renderGuardianAssignmentAssignedCopy,
  renderGuardianAssignmentDueSoonCopy,
  renderGuardianAssignmentGradedCopy,
  renderGuardianChallengeAssignedCopy,
  renderGuardianChallengeDueSoonCopy,
  renderGuardianContentSharedCopy,
} from './guardian'
import { renderPremiumAccessRequestedRecipientCopy } from './premium'
import { renderShareActorCopy, renderShareRecipientCopy } from './share'

export * from './types'

/** Keyed by `${type}:${audience}`. */
const REGISTRY: Record<string, CopyRenderer> = {
  [`${NOTIFICATION_TYPE.CONTENT_SHARED}:RECIPIENT`]: renderShareRecipientCopy,
  [`${NOTIFICATION_TYPE.CONTENT_SHARED}:ACTOR`]: renderShareActorCopy,

  // Assignment notifications have no ACTOR side: the teacher's own view of a course is a
  // roll-up they *receive* ("5 siswa belum..."), not a record of something they sent.
  [`${NOTIFICATION_TYPE.ASSIGNMENT_ASSIGNED}:RECIPIENT`]: renderAssignmentAssignedCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_GRADED}:RECIPIENT`]: renderAssignmentGradedCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_DUE_SOON}:RECIPIENT`]: renderAssignmentDueSoonCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_NEEDS_GRADING}:RECIPIENT`]: renderAssignmentNeedsGradingCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_NOT_STARTED}:RECIPIENT`]: renderAssignmentNotStartedCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_INCOMPLETE}:RECIPIENT`]: renderAssignmentIncompleteCopy,
  [`${NOTIFICATION_TYPE.ASSIGNMENT_ALL_COMPLETED}:RECIPIENT`]: renderAssignmentAllCompletedCopy,
  [`${NOTIFICATION_TYPE.CHALLENGE_ASSIGNED}:RECIPIENT`]: renderChallengeAssignedCopy,
  [`${NOTIFICATION_TYPE.CHALLENGE_DUE_SOON}:RECIPIENT`]: renderChallengeDueSoonCopy,
  // The teacher's own send is an ACTOR receipt and deliberately does not light the badge; the
  // outcomes are RECIPIENT rows, because the school does need to be pulled back in for those.
  'connect.requested:ACTOR': renderConnectRequestedActorCopy,
  'connect.requested:RECIPIENT': renderConnectRequestedRecipientCopy,
  'connect.accepted:RECIPIENT': renderConnectAcceptedRecipientCopy,
  'connect.rejected:RECIPIENT': renderConnectRejectedRecipientCopy,
  // Teacher -> parent nudge. RECIPIENT only: the teacher's confirmation is the send toast, not a
  // card, because unlike a connect request this one always reaches an account that exists.
  'connect.parent_message:RECIPIENT': renderConnectParentMessageRecipientCopy,
  // The two halves of a removal. RECIPIENT only in both directions: the actor already sees a toast,
  // and neither actor needs to be pulled back into their own feed for an action they just took.
  'connect.student_removed:RECIPIENT': renderConnectStudentRemovedRecipientCopy,
  'connect.removal_requested:RECIPIENT': renderConnectRemovalRequestedRecipientCopy,
  // Parent -> school identity edits on a dual-connected child. Both audiences: the school's
  // owner/admin/guru kelas get the RECIPIENT card, the parent gets an ACTOR receipt of their own.
  'connect.child_pin_updated:RECIPIENT': renderConnectChildPinUpdatedRecipientCopy,
  'connect.child_pin_updated:ACTOR': renderConnectChildPinUpdatedActorCopy,
  'connect.child_name_updated:RECIPIENT': renderConnectChildNameUpdatedRecipientCopy,
  'connect.child_name_updated:ACTOR': renderConnectChildNameUpdatedActorCopy,
  // Child -> the adults responsible for them. RECIPIENT only: the event is a roll-up shared by
  // every child in one audience, so an ACTOR row would show one child a card about another. The
  // child's own confirmation is the toast in the reader.
  'premium.access_requested:RECIPIENT': renderPremiumAccessRequestedRecipientCopy,
  // Teacher -> their school's owners and admins. RECIPIENT only, same reason as the premium
  // roll-up: the event is shared by every teacher waiting for a rombel, so an ACTOR row would show
  // one of them a card about another. The teacher's confirmation is the toast on the blocker.
  'assignment.class_requested:RECIPIENT': renderAssignmentClassRequestedRecipientCopy,

  // Cermin orang tua. RECIPIENT only, and deliberately not an extra audience on the child's own
  // event: the copy above is second person, and one event cannot carry two sentences.
  [`${NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_ASSIGNED}:RECIPIENT`]: renderGuardianAssignmentAssignedCopy,
  [`${NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_GRADED}:RECIPIENT`]: renderGuardianAssignmentGradedCopy,
  [`${NOTIFICATION_TYPE.GUARDIAN_ASSIGNMENT_DUE_SOON}:RECIPIENT`]: renderGuardianAssignmentDueSoonCopy,
  [`${NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_ASSIGNED}:RECIPIENT`]: renderGuardianChallengeAssignedCopy,
  [`${NOTIFICATION_TYPE.GUARDIAN_CHALLENGE_DUE_SOON}:RECIPIENT`]: renderGuardianChallengeDueSoonCopy,
  [`${NOTIFICATION_TYPE.GUARDIAN_CONTENT_SHARED}:RECIPIENT`]: renderGuardianContentSharedCopy,
}

/**
 * Renders a notification's sentence.
 *
 * Unknown types must degrade rather than throw — the server can emit a type
 * before an older client build knows about it.
 */
export function renderNotificationCopy(view: NotificationView): NotificationCopy {
  const renderer = REGISTRY[`${view.type}:${view.audience}`]
  if (renderer) return renderer(view)

  return {
    body: [{ text: view.snapshot.title ?? 'Ada pembaruan untukmu.' }],
    contextLine: view.context.courseName,
  }
}
